import {
  Button,
  Card,
  Checkbox,
  Flex,
  Header,
  InlineInput,
  RadioGroup,
  RadioItem,
} from '@driftt/dds'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import {
  AIPrompt,
  Attributes,
  AttributesSection,
  LeavingModal,
} from 'components/AIConfiguration/LeadQualification/PersonaCard/components'
import { PersonaConfig } from 'components/AIConfiguration/LeadQualification/types'
import {
  AILQTrackAction,
  useLQTracking,
} from 'components/AIConfiguration/LeadQualification/useLQTracking'
import { useGPTQualificationAttributes } from 'hooks/useGPTQualificationAttributes'
import { PropertyDefinition } from 'types/contacts'

import { PROMPT_MODES, QUALIFICATION_DEFAULT_NAME } from '../constants'
import ConfirmationModal from './components/ConfirmationModal'
import {
  buildAttributesPayload,
  getDifference,
  transformInitialValues,
} from './helpers'
import { PersonaForm, PersonaFormAttributes } from './types'

interface Props {
  persona?: PersonaConfig
  onPersonaSave?: () => void
  contactAttributes: PropertyDefinition[]
}

const PersonaCard: React.FC<Props> = ({
  persona,
  onPersonaSave,
  contactAttributes,
}) => {
  const personaContainer = useRef<HTMLFormElement>(null)
  const { trackLQ } = useLQTracking()

  const { handleSavePersona, generateAIPrompt } = useGPTQualificationAttributes(
    {
      onSavePersonaSuccess: onPersonaSave,
    }
  )
  const [differences, setDifferences] = useState<
    | { changed: PersonaFormAttributes[]; deleted: PersonaFormAttributes[] }
    | undefined
  >(undefined)
  const [canEditAIAttributes, setCanEditAIAttributes] = useState(false)
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [leaveModalConfig, setLeaveModalConfig] = useState({
    open: false,
    nextHref: '',
  })

  const defaultValues = useMemo(
    () => transformInitialValues({ data: persona, contactAttributes }),
    [persona, contactAttributes]
  )

  const methods = useForm<PersonaForm>({
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  })

  const { dirtyFields, isValid } = methods.formState

  const mode = methods.watch('mode')
  const attributes = methods.watch('attributes')
  const hasAcknowledge = methods.watch('hasAcknowledge')

  const updatedName = dirtyFields.name
  const hasAIMode = mode === PROMPT_MODES.AI
  const isDefaultStateUpdated =
    JSON.stringify(attributes) !== JSON.stringify(defaultValues.attributes)
  const _hasAttributesChanges = useRef<boolean>(false)

  useEffect(() => {
    _hasAttributesChanges.current = isDefaultStateUpdated
  }, [isDefaultStateUpdated])

  const isSubmitEnabled =
    (!!hasAcknowledge && isDefaultStateUpdated && isValid) ||
    (!isDefaultStateUpdated && !!updatedName)
  const canEditAttributes = (hasAIMode && canEditAIAttributes) || !hasAIMode
  const isCheckboxDisabled = !isDefaultStateUpdated || isLoadingAttributes
  const isRadioDisabled = isDefaultStateUpdated || isLoadingAttributes

  const onSubmit = (data: PersonaForm) => {
    const attributesPayload = buildAttributesPayload(data.attributes)

    handleSavePersona.mutate(
      {
        id: persona?.id,
        name: data.name || QUALIFICATION_DEFAULT_NAME,
        prompt: data.prompt || '',
        attributes: attributesPayload,
      },
      {
        onSuccess: () => setOpenConfirmation(false),
      }
    )

    trackLQ(AILQTrackAction.SAVE_ATTRIBUTES, {
      mode: data.mode,
      attributes: attributesPayload,
    })

    methods.reset({
      ...data,
      foundAIAttributes: hasAIMode ? data.attributes : undefined,
      hasAcknowledge: false,
    })

    setCanEditAIAttributes(false)
  }

  const generateNewPromptText = ({
    onError,
    onSuccess,
  }: {
    onError?: () => void
    onSuccess?: () => void
  }) => {
    setIsLoadingAttributes(true)
    generateAIPrompt.mutate(buildAttributesPayload(attributes), {
      onSuccess: (promptText) => {
        methods.setValue('prompt', promptText)
        if (onSuccess) onSuccess()
      },
      onError: () => {
        if (onError) onError()
      },
      onSettled: () => {
        setIsLoadingAttributes(false)
        methods.resetField('prompt', { keepDirty: false })
      },
    })
  }

  const handleChangesInAttributes = () => {
    setDifferences(getDifference(defaultValues.attributes, attributes))
    setOpenConfirmation(true)
  }

  const handleNoChangesInAttributes = () => methods.handleSubmit(onSubmit)()

  const handleCancelAttributesEdition = () => {
    setCanEditAIAttributes(false)
    methods.resetField('attributes')
  }

  const handleCancelEdition = () => {
    setCanEditAIAttributes(false)
    methods.reset(defaultValues)
  }

  const handleContinue = () => {
    const foundAIAttributes = methods.getValues('foundAIAttributes')

    const shouldGenerateAIPrompt =
      hasAIMode &&
      foundAIAttributes &&
      JSON.stringify(attributes) !== JSON.stringify(foundAIAttributes)

    if (shouldGenerateAIPrompt)
      generateNewPromptText({
        onSuccess: () => {
          methods.handleSubmit(onSubmit)()
        },
      })
    else methods.handleSubmit(onSubmit)()
  }

  const handleSave = async () => {
    if (!(await methods.trigger())) return

    if (isDefaultStateUpdated) handleChangesInAttributes()
    else handleNoChangesInAttributes()
  }

  const handleNameEmpty = () => {
    const value = methods.getValues('name') || QUALIFICATION_DEFAULT_NAME
    methods.setValue('name', value, { shouldDirty: true })
  }

  const handleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    const anchorTag = (target && target.closest('a')) || null
    if (
      personaContainer.current &&
      !personaContainer.current.contains(target) &&
      anchorTag instanceof HTMLAnchorElement &&
      anchorTag.href
    ) {
      if (_hasAttributesChanges.current) {
        e.preventDefault()
        e.stopPropagation()
        setLeaveModalConfig({
          open: true,
          nextHref: anchorTag.href,
        })
      }
    }
  }

  useEffect(() => {
    methods.setValue('hasAcknowledge', false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(attributes)])

  useEffect(() => {
    methods.register('foundAIAttributes')
    document.addEventListener('click', handleLinkClick)
    return () => {
      methods.unregister('foundAIAttributes')
      document.removeEventListener('click', handleLinkClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FormProvider {...methods}>
      <Flex
        ref={personaContainer}
        as="form"
        justifyContent="center"
        fullHeight
        fullWidth
      >
        <Card
          css={{ width: '100%', maxWidth: '810px', padding: '$3', gap: '$3' }}
        >
          <Card.Header>
            <Flex justifyContent="space-between">
              <Header variant="h3" onBlur={handleNameEmpty}>
                <Controller
                  name="name"
                  control={methods.control}
                  render={({ field: { value, onChange } }) => (
                    <InlineInput label="" value={value} onChange={onChange} />
                  )}
                />
              </Header>
            </Flex>
          </Card.Header>
          <Card.Content>
            <Controller
              name="mode"
              control={methods.control}
              render={({ field: { value, onChange } }) => (
                <RadioGroup
                  label="How do you want to define the qualification attributes?"
                  value={value}
                  onValueChange={(newValue) => {
                    if (newValue === PROMPT_MODES.AI)
                      generateNewPromptText({
                        onError: () =>
                          methods.setValue('mode', PROMPT_MODES.MANUAL),
                      })
                    onChange(newValue)
                  }}
                  disabled={isRadioDisabled}
                >
                  <Flex css={{ marginBottom: 24 }} direction="column">
                    <RadioItem
                      value={PROMPT_MODES.AI}
                      label="Describe qualification criteria using natural language "
                      description="Drift will convert this into a set of attributes for your review"
                    />
                    <RadioItem
                      value={PROMPT_MODES.MANUAL}
                      label="Manually add attributes"
                    />
                  </Flex>
                </RadioGroup>
              )}
            />
            {hasAIMode && (
              <AIPrompt
                disabled={canEditAttributes}
                isLoading={isLoadingAttributes}
                onGeneratingAttributes={({ isLoading }) =>
                  setIsLoadingAttributes(isLoading)
                }
              />
            )}
            <AttributesSection
              isEditing={canEditAttributes}
              onEdit={() => setCanEditAIAttributes(true)}
              onCancel={handleCancelAttributesEdition}
              disabled={isLoadingAttributes}
              isCard={hasAIMode}
            >
              <Attributes
                disabled={!canEditAttributes || isLoadingAttributes}
                isLoading={isLoadingAttributes}
                contactAttributes={contactAttributes}
              />
            </AttributesSection>
          </Card.Content>
          <Controller
            name="hasAcknowledge"
            control={methods.control}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                label="I understand that these changes will affect every instance where the AI lead qualification skill is used."
                checked={value}
                onChange={onChange}
                disabled={isCheckboxDisabled}
              />
            )}
          />
          <Flex justifyContent="flex-end">
            <Button
              variant="secondary"
              onClick={handleCancelEdition}
              disabled={!isDefaultStateUpdated}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={handleSavePersona.isLoading}
              disabled={!isSubmitEnabled}
            >
              Save
            </Button>
          </Flex>
        </Card>
      </Flex>

      <ConfirmationModal
        isNew={!persona}
        aiMode={mode}
        isLoading={generateAIPrompt.isLoading || handleSavePersona.isLoading}
        changed={differences?.changed || []}
        deleted={differences?.deleted || []}
        isOpen={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleContinue={handleContinue}
      />

      <LeavingModal
        open={leaveModalConfig.open}
        handleExit={() => {
          window.location.href = new URL(leaveModalConfig.nextHref).href
        }}
        handleContinue={() => {
          setLeaveModalConfig({ nextHref: '', open: false })
        }}
        handleSave={() => {
          handleContinue()
          setLeaveModalConfig({ nextHref: '', open: false })
        }}
      />
    </FormProvider>
  )
}

export default PersonaCard
